import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ShieldCheck, LogOut } from "lucide-react";

export default function ImpersonationBanner() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    const read = () => {
      const raw = localStorage.getItem("aip_impersonating");
      if (!raw) {
        setTarget(null);
        return;
      }
      try {
        setTarget(JSON.parse(raw));
      } catch {
        setTarget(null);
      }
    };
    read();
    const onStorage = () => read();
    window.addEventListener("storage", onStorage);
    // Also react when other parts of the app update localStorage in the same tab
    const i = setInterval(read, 1000);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(i);
    };
  }, []);

  if (!target) return null;

  const stop = async () => {
    const adminToken = localStorage.getItem("aip_admin_token");
    if (!adminToken) {
      toast.error("Admin session was not preserved. Please sign in again.");
      localStorage.removeItem("aip_token");
      localStorage.removeItem("aip_impersonating");
      await refresh();
      navigate("/login");
      return;
    }
    localStorage.setItem("aip_token", adminToken);
    localStorage.removeItem("aip_admin_token");
    localStorage.removeItem("aip_impersonating");
    setTarget(null);
    await refresh();
    toast.success("Back to your admin session");
    navigate("/admin");
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[60] w-full border-b border-amber-500/60 bg-amber-500 text-amber-950"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-6 py-2 text-sm">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          <span className="font-semibold">
            Impersonating {target.name || target.email}
          </span>
          <span className="hidden text-amber-900/80 sm:inline">
            · all actions here are performed as this user
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          data-testid="impersonation-exit-btn"
          onClick={stop}
          className="h-8 rounded-sm bg-amber-950 text-amber-50 hover:bg-amber-900 hover:text-white"
        >
          <LogOut className="mr-1.5 h-3.5 w-3.5" />
          Exit impersonation
        </Button>
      </div>
    </div>
  );
}
